import React, { useLayoutEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, IconButton, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import PdfIcon from "@/components/Icons/PdfIcon";
import RawIcon from "@/components/Icons/RawIcon";
import If from "@/components/layout/If";
import { colors } from "@/theme/overrides/colors";

import { getThumbnailFromDataURL } from "../utils/helpers";

export interface AttachmentFile {
  /**
   * For react key only.
   */
  _id: string;
  signedId?: string;
  file: File;
}

interface Props {
  attachment: AttachmentFile;
  onRemove: () => void;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 80,
    minWidth: 80,
    marginTop: 10,
    border: "1px solid #dee6f0",
  },
  remove: {
    position: "absolute",
    top: -10,
    right: -10,
    color: colors.white,
    backgroundColor: "#112340b2",
    "&:hover": {
      backgroundColor: "#11234099",
    },
  },
  preview: {
    width: 80,
    height: 80,
  },
  previewImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  file: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 4),
  },
  icon: {
    width: 34,
    height: 34,
    marginRight: theme.spacing(2),
  },
  filename: {
    whiteSpace: "nowrap",
  },
  uploading: {
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
    backgroundColor: "#00000080", // black, 50% opacity
  },
}));

const ToolbarFile: React.FC<Props> = ({ attachment, onRemove }) => {
  const { classes } = useStyles();
  const [thumbnail, setThumbnail] = useState(
    URL.createObjectURL(attachment.file)
  );

  const resource = attachment.file.type.split("/")[0];
  const showThumbnail = resource === "image" || resource === "video";

  useLayoutEffect(() => {
    if (resource === "video") {
      getThumbnailFromDataURL(thumbnail, 80, 80).then(setThumbnail);
    }
  }, []);

  const notUploaded = !attachment.signedId;

  return (
    <div className={classes.root}>
      <IconButton className={classes.remove} size="small" onClick={onRemove}>
        <CloseIcon fontSize="small" />
      </IconButton>
      {showThumbnail ? (
        <>
          <div className={classes.preview}>
            <img className={classes.previewImg} src={thumbnail} />
          </div>
          {notUploaded && (
            <div className={classes.uploading}>
              <CircularProgress size={34} />
            </div>
          )}
        </>
      ) : (
        <div className={classes.file}>
          <div className={classes.icon}>
            {notUploaded ? (
              <CircularProgress size={34} />
            ) : (
              <If
                condition={attachment.file.type === "application/pdf"}
                then={<PdfIcon />}
                else={<RawIcon />}
              />
            )}
          </div>

          <Typography className={classes.filename}>
            {attachment.file.name}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default ToolbarFile;
